import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import LoadingOverlay from '../LoadingOverlay';
import Toast from '../Toast';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';

const CookPerformance = ({ cookId, restaurantId: restaurantIdProp }) => {
    const restaurantId = normalizeRestaurantId(restaurantIdProp);
    const [performance, setPerformance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [dateRange, setDateRange] = useState('today');

    useEffect(() => {
        if (cookId && restaurantId) {
            fetchPerformance();
        }
    }, [cookId, restaurantId, dateRange]);

    const fetchPerformance = async () => {
        try {
            setLoading(true);
            const today = new Date();
            let startDate, endDate;

            switch (dateRange) {
                case 'today':
                    startDate = new Date(today);
                    startDate.setHours(0, 0, 0, 0);
                    endDate = new Date(today);
                    endDate.setHours(23, 59, 59, 999);
                    break;
                case 'week':
                    startDate = new Date(today);
                    startDate.setDate(today.getDate() - 7);
                    endDate = new Date(today);
                    break;
                case 'month':
                    startDate = new Date(today);
                    startDate.setMonth(today.getMonth() - 1);
                    endDate = new Date(today);
                    break;
                default:
                    startDate = new Date(today);
                    startDate.setHours(0, 0, 0, 0);
                    endDate = new Date(today);
                    endDate.setHours(23, 59, 59, 999);
            }

            const response = await axios.get(API_ENDPOINTS.COOK_PERFORMANCE(cookId), {
                params: {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                }
            });

            setPerformance(response.data);
        } catch (error) {
            console.error('Failed to fetch performance:', error);
            showToast('Failed to load performance data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading performance data...</p>
                </div>
            </div>
        );
    }

    const aggregate = performance?.aggregate || {};

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Header with Date Range */}
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1">Performance Metrics</h2>
                        <p className="text-sm text-gray-600">Track your cooking performance and ratings</p>
                    </div>
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="px-4 py-2 border-2 border-gray-300 rounded-lg font-medium focus:border-orange-500 focus:outline-none text-sm sm:text-base"
                    >
                        <option value="today">Today</option>
                        <option value="week">Last 7 Days</option>
                        <option value="month">Last 30 Days</option>
                    </select>
                </div>
            </div>

            {!performance ? (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                    <div className="text-6xl mb-4">📊</div>
                    <p className="text-xl text-gray-600 mb-2">No performance data available</p>
                    <p className="text-sm text-gray-500">Complete orders to see your performance metrics</p>
                </div>
            ) : (
                <>
                    {/* Main Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl shadow-lg p-4 sm:p-6 text-white">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-3xl sm:text-4xl">🍳</div>
                                <div className="text-right">
                                    <div className="text-2xl sm:text-3xl font-bold">{aggregate.totalOrdersPrepared || 0}</div>
                                    <div className="text-xs sm:text-sm opacity-90">Orders Prepared</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl shadow-lg p-4 sm:p-6 text-white">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-3xl sm:text-4xl">⏱️</div>
                                <div className="text-right">
                                    <div className="text-2xl sm:text-3xl font-bold">
                                        {aggregate.avgPrepTime > 0 ? `${aggregate.avgPrepTime}m` : 'N/A'}
                                    </div>
                                    <div className="text-xs sm:text-sm opacity-90">Avg Prep Time</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg p-4 sm:p-6 text-white">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-3xl sm:text-4xl">✅</div>
                                <div className="text-right">
                                    <div className="text-2xl sm:text-3xl font-bold">
                                        {aggregate.onTimeCompletionRate > 0 
                                            ? `${aggregate.onTimeCompletionRate}%` 
                                            : 'N/A'}
                                    </div>
                                    <div className="text-xs sm:text-sm opacity-90">On-Time Rate</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-yellow-500 to-amber-500 rounded-xl shadow-lg p-4 sm:p-6 text-white">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-3xl sm:text-4xl">⭐</div>
                                <div className="text-right">
                                    <div className="text-2xl sm:text-3xl font-bold">
                                        {aggregate.avgFoodRating > 0 
                                            ? `${aggregate.avgFoodRating}/5` 
                                            : 'N/A'}
                                    </div>
                                    <div className="text-xs sm:text-sm opacity-90">Food Rating</div>
                                </div>
                            </div>
                            {aggregate.avgFoodRating > 0 && (
                                <div className="mt-3 flex gap-1 justify-end">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <span
                                            key={star}
                                            className={`text-lg ${
                                                star <= aggregate.avgFoodRating
                                                    ? 'text-yellow-200'
                                                    : 'text-white/30'
                                            }`}
                                        >
                                            ★
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Rework Warning */}
                    {aggregate.totalReworkCount > 0 && (
                        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 sm:p-6">
                            <div className="flex items-center gap-3">
                                <span className="text-3xl">⚠️</span>
                                <div>
                                    <h4 className="font-bold text-red-900 mb-1">Rework Count</h4>
                                    <p className="text-red-800 text-sm sm:text-base">
                                        {aggregate.totalReworkCount} order{aggregate.totalReworkCount > 1 ? 's' : ''} required rework
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Performance History Table */}
                    {performance.performances && performance.performances.length > 0 && (
                        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
                            <h3 className="text-lg sm:text-xl font-bold mb-4 text-gray-800">Recent Performance History</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b-2 border-gray-200">
                                            <th className="text-left p-2 sm:p-3 font-semibold text-gray-700">Date</th>
                                            <th className="text-right p-2 sm:p-3 font-semibold text-gray-700">Orders</th>
                                            <th className="text-right p-2 sm:p-3 font-semibold text-gray-700">Avg Time</th>
                                            <th className="text-right p-2 sm:p-3 font-semibold text-gray-700">Rework</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {performance.performances.slice(0, 10).map((perf, idx) => (
                                            <tr key={idx} className="border-b border-gray-100 hover:bg-orange-50 transition-colors">
                                                <td className="p-2 sm:p-3 font-medium">
                                                    {new Date(perf.date).toLocaleDateString('en-US', { 
                                                        month: 'short', 
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </td>
                                                <td className="text-right p-2 sm:p-3 font-semibold text-gray-800">
                                                    {perf.ordersPrepared || 0}
                                                </td>
                                                <td className="text-right p-2 sm:p-3 text-gray-700">
                                                    {perf.avgPrepTime ? `${perf.avgPrepTime}m` : 'N/A'}
                                                </td>
                                                <td className="text-right p-2 sm:p-3">
                                                    {perf.reworkCount ? (
                                                        <span className="text-red-600 font-semibold">{perf.reworkCount}</span>
                                                    ) : (
                                                        <span className="text-green-600">0</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Info Box */}
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-4 sm:p-6">
                        <div className="flex items-start gap-3">
                            <span className="text-2xl sm:text-3xl">💡</span>
                            <div>
                                <h4 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">Performance Metrics Explained</h4>
                                <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                                    <li>• <strong>Orders Prepared:</strong> Total orders you've completed</li>
                                    <li>• <strong>Avg Prep Time:</strong> Average time to prepare orders</li>
                                    <li>• <strong>On-Time Rate:</strong> Percentage of orders completed within 30 minutes</li>
                                    <li>• <strong>Food Rating:</strong> Average customer rating for your prepared food</li>
                                    <li>• <strong>Rework Count:</strong> Orders that needed to be remade</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default CookPerformance;
